type PostHogEvent = {
	event?: string;
	properties?: Record<string, unknown>;
};

const SENSITIVE_VALUE = /(?:\b(?:bearer|token|api[_-]?key|password|secret)\b\s*[:=]?\s*)[^\s,;]+/gi;
const EMAIL = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const SENSITIVE_KEY = /\b(?:authorization|bearer|token|api[_-]?key|password|secret|cookie)\b/i;

function scrub(value: unknown, key?: string): unknown {
	if (key && SENSITIVE_KEY.test(key)) return '[redacted]';
	if (typeof value === 'string') {
		return value.replace(EMAIL, '[redacted-email]').replace(SENSITIVE_VALUE, '[redacted]');
	}
	if (Array.isArray(value)) return value.map((item) => scrub(item));
	if (value && typeof value === 'object') {
		return Object.fromEntries(
			Object.entries(value).map(([itemKey, item]) => [itemKey, scrub(item, itemKey)]),
		);
	}
	return value;
}

/**
 * PostHog's browser exception capture runs after this hook. Only `$exception_list` (the
 * actual error messages + stack traces, where app content — and so a leaked credential —
 * could end up) is scrubbed; PostHog's own properties (`token`, `$lib_rate_limit_remaining_tokens`,
 * session/device ids, etc.) are left alone. An earlier version scrubbed the whole properties
 * object and silently corrupted `token` (PostHog's own project key, duplicated into properties)
 * and `$lib_rate_limit_remaining_tokens` (a number, overwritten with the string "[redacted]") —
 * PostHog accepted the mangled event with 200 but dropped it during processing, so no frontend
 * exception ever actually landed.
 */
export function scrubPostHogErrorEvent<T extends PostHogEvent | null>(event: T): T {
	if (!event || event.event !== '$exception' || !event.properties) return event;

	const properties = { ...event.properties };
	if ('$exception_list' in properties) {
		properties.$exception_list = scrub(properties.$exception_list);
	}
	const currentUrl = properties.$current_url;
	if (typeof currentUrl === 'string') {
		try {
			const url = new URL(currentUrl);
			properties.$current_url = `${url.origin}${url.pathname}`;
		} catch {
			properties.$current_url = '[redacted-url]';
		}
	}

	return { ...event, properties };
}
